import { cx } from "class-variance-authority";
import type { PropsWithChildren } from "react";
import { Button, DialogTrigger, Popover, type PopoverProps } from "react-aria-components";
import useMeasure from "react-use-measure";

interface DropdownProps extends PropsWithChildren {
  trigger?: React.ReactNode;
  customTrigger?: React.ReactNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  popoverProps?: PopoverProps;
  "data-testid"?: string;
  btnClassName?: string;
  triggerClassName?: string;
  useTriggerWidth?: boolean;
}

export const Dropdown = ({
  trigger,
  customTrigger,
  children,
  isOpen,
  setIsOpen,
  popoverProps,
  "data-testid": dataTestId,
  btnClassName,
  triggerClassName,
  useTriggerWidth = true,
}: DropdownProps) => {
  const [triggerRef, { width }] = useMeasure();

  let element: React.ReactNode = null;
  if (customTrigger) {
    element = customTrigger;
  } else {
    element = (
      <Button
        className={cx("flex w-fit justify-start outline-none", btnClassName)}
        data-testid={dataTestId}
      >
        {trigger}
      </Button>
    );
  }

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <div
        ref={triggerRef}
        className={cx("w-full", triggerClassName)}
      >
        {element}
      </div>
      <Popover
        {...popoverProps}
        className={cx(
          "w-60 rounded-input-rounding border border-elevation-outline-1 bg-elevation-surface-1 p-5 shadow-1",
          popoverProps?.className,
        )}
        style={{ width: (useTriggerWidth && width) || undefined }}
      >
        {children}
      </Popover>
    </DialogTrigger>
  );
};
